/**
 * PublicRoute Component
 *
 * Route wrapper for authentication pages (login, register, password reset).
 * Redirects authenticated users to dashboard while allowing unauthenticated access.
 */

// 1. External dependencies
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

// 2. Internal absolute imports
import { useAuth } from '@/hooks/useAuth';

export interface PublicRouteProps {
  /** Auth form content (LoginForm, RegisterForm, etc.) */
  children: React.ReactNode;
}

/**
 * Public Route Component
 *
 * Wraps authentication routes (login, register, password reset). Features:
 * - Checks authentication status using useAuth hook
 * - Shows loading state while auth status is being determined
 * - Redirects authenticated users to dashboard or intended destination
 * - Renders children when user is not authenticated
 *
 * Usage:
 * ```tsx
 * <PublicRoute>
 *   <LoginForm />
 * </PublicRoute>
 * ```
 */
export function PublicRoute({ children }: PublicRouteProps): React.JSX.Element {
  const { authState } = useAuth();
  const location = useLocation();

  // Get the intended destination from location state, default to dashboard
  const from = (location.state as { from?: string })?.from || '/dashboard';

  // Show loading state while authentication status is being determined
  if (authState.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-default-500">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Redirect to intended destination if already authenticated
  if (authState.isAuthenticated) {
    return (
      <Navigate
        to={from}
        replace
      />
    );
  }

  // Render auth form when not authenticated
  return <>{children}</>;
}